import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useNavScroll } from "../hooks/useNavScroll";
import { useLiveInfo } from "../hooks/useLiveInfo";
import { fetchEvent } from "../api/client";
import { isBroadcastLive } from "../lib/live-display";
import { useAuth } from "../context/AuthContext";
import { useColorMode } from "../context/ColorModeContext";

/** Header essentials only — secondary destinations live in footer / landing explore. */
const LINKS: { to: string; label: string }[] = [
  { to: "/dorothy", label: "About us" },
  { to: "/blog", label: "Blog" },
  { to: "/gallery", label: "Gallery" },
  { to: "/videos", label: "Videos" },
  { to: "/participate", label: "Participate" },
  { to: "/give", label: "Give" },
];

function NavLiveCta({
  live,
  className,
  onClick,
}: {
  live: boolean;
  className: string;
  onClick?: () => void;
}) {
  return (
    <NavLink to="/live" className={className} onClick={onClick}>
      {live ? "Watch Live" : "Watch"}
      {live && <span className="live-dot" aria-hidden="true" />}
    </NavLink>
  );
}

function useOvernightOngoing(): boolean {
  const [ongoing, setOngoing] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const event = await fetchEvent();
        if (!cancelled) setOngoing(event.eventPhase === "ongoing");
      } catch {
        if (!cancelled) setOngoing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return ongoing;
}

function NavCheckInLink({ className, onClick }: { className?: string; onClick?: () => void }) {
  const { user } = useAuth();
  const ongoing = useOvernightOngoing();
  if (!user) return null;
  // Always offer the page; emphasize when overnight is ongoing.
  return (
    <Link
      to="/check-in"
      className={className ?? "nav-checkin-link"}
      onClick={onClick}
      title={ongoing ? "Check in for tonight" : "Member check-in"}
    >
      {ongoing ? "Check in" : "Check in"}
    </Link>
  );
}


export function Nav() {
  const scrolled = useNavScroll();
  const liveInfo = useLiveInfo();
  const live = isBroadcastLive(liveInfo);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  return (
    <>
      <header className={`nav${scrolled ? " scrolled" : ""}`} id="nav">
        <Link to="/" className="brand" aria-label="Together In Heaven">
          <img src="/assets/logo.png" alt="Together In Heaven" className="brand-logo" />
        </Link>

        <button
          type="button"
          className="nav-menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="nav-drawer"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span className="nav-menu-toggle-icon" aria-hidden="true" />
        </button>

        <nav className="nav-links" aria-label="Main">
          {LINKS.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => (isActive ? "is-active" : undefined)}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="nav-right">
          <NavCheckInLink />
          <NavLiveCta live={live} className="nav-cta" />
          <UserChip />
        </div>
      </header>

      {menuOpen && (
        <>
          <div
            className="nav-drawer-backdrop"
            onClick={() => setMenuOpen(false)}
            role="presentation"
          />
          <aside
            id="nav-drawer"
            className="nav-drawer"
            aria-label="Mobile navigation"
          >
            <nav className="nav-drawer-links">
              {LINKS.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => (isActive ? "is-active" : undefined)}
                  onClick={() => setMenuOpen(false)}
                >
                  {label}
                </NavLink>
              ))}
            </nav>
            <div className="nav-drawer-footer">
              <NavLiveCta
                live={live}
                className="nav-drawer-cta"
                onClick={() => setMenuOpen(false)}
              />
              <DrawerAuth onNavigate={() => setMenuOpen(false)} />
            </div>
          </aside>
        </>
      )}
    </>
  );
}

function DrawerAuth({ onNavigate }: { onNavigate: () => void }) {
  const { user, isAdmin, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) return null;

  if (!user) {
    return (
      <Link
        to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`}
        className="nav-drawer-signin"
        onClick={onNavigate}
      >
        Sign in
      </Link>
    );
  }

  const first = user.name.split(" ")[0] || user.email;

  return (
    <div className="nav-drawer-user">
      <div className="nav-drawer-user-name">{user.name}</div>
      <div className="nav-drawer-user-email">{user.email}</div>
      <Link to="/check-in" className="nav-drawer-link" onClick={onNavigate}>
        Check in
      </Link>
      {isAdmin && (
        <Link to="/admin" className="nav-drawer-link" onClick={onNavigate}>
          Admin dashboard
        </Link>
      )}
      <button
        type="button"
        className="nav-drawer-link"
        onClick={async () => {
          await logout();
          onNavigate();
          navigate("/");
        }}
      >
        Sign out ({first})
      </button>
    </div>
  );
}

function UserChip() {
  const { user, isAdmin, logout, loading } = useAuth();
  const { mode, setMode } = useColorMode();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (loading) return <div className="user-chip user-chip-loading" aria-hidden="true" />;

  if (!user) {
    return (
      <Link
        to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`}
        className="user-chip user-chip-signin"
      >
        Sign in
      </Link>
    );
  }

  const first = user.name.split(" ")[0] || user.email;

  return (
    <div className="user-chip-wrap" ref={ref}>
      <button
        type="button"
        className="user-chip"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="user-chip-avatar" aria-hidden="true">
          {first.slice(0, 1).toUpperCase()}
        </span>
        <span className="user-chip-name">{first}</span>
        <span className="user-chip-caret" aria-hidden="true">v</span>
      </button>
      {open && (
        <div className="user-menu" role="menu">
          <div className="user-menu-header">
            <div className="user-menu-name">{user.name}</div>
            <div className="user-menu-email">{user.email}</div>
            {isAdmin && <div className="user-menu-badge">Admin</div>}
          </div>
          <div className="user-menu-section">
            <div className="user-menu-section-label" id="user-menu-appearance">
              Appearance
            </div>
            <div
              className="user-menu-theme"
              role="group"
              aria-labelledby="user-menu-appearance"
            >
              <button
                type="button"
                className={`user-menu-theme-btn${mode === "dark" ? " is-active" : ""}`}
                aria-pressed={mode === "dark"}
                onClick={() => setMode("dark")}
              >
                Dark
              </button>
              <button
                type="button"
                className={`user-menu-theme-btn${mode === "light" ? " is-active" : ""}`}
                aria-pressed={mode === "light"}
                onClick={() => setMode("light")}
              >
                Light
              </button>
            </div>
          </div>
          <Link to="/check-in" className="user-menu-item" role="menuitem">
            Check in
          </Link>
          {isAdmin && (
            <Link to="/admin" className="user-menu-item" role="menuitem">
              Admin dashboard
            </Link>
          )}
          <button
            type="button"
            className="user-menu-item"
            role="menuitem"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
